import { isEmpty } from '@newfront-insurance/core';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useMemo, useRef } from 'react';

import { OrphanedStreamRecovery } from './orphaned-stream-recovery';
import type { GetChatsByFiltersChat } from '../../../queries/chats';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { NovaChatProvider } from '../providers';

interface OrphanedStreamsContainerProps {
  chats: GetChatsByFiltersChat[];
}

/**
 * Container component that manages recovery of orphaned streams.
 * Detects chats with activeStreamId (excluding the currently active chat)
 * and creates renderless recovery components for each one.
 *
 * Each orphaned stream gets its own NovaChatProvider instance to ensure
 * proper isolation and stream resumability.
 */
export function OrphanedStreamsContainer({ chats }: OrphanedStreamsContainerProps) {
  const { chatUuid, userUuid, authProvider, userScopes, notificationController } = useChatOverlayContext();
  const chatsRef = useRef(chats);

  // Find orphaned streams: chats with activeStreamId that are not the current chat
  const orphanedStreams = useMemo(() => {
    return chatsRef.current.filter((chat) => {
      if (!chat.uuid) {
        return false;
      }
      const hasActiveStream = chat.activeStreamId !== null;
      const chatUuidWithoutPrefix = chatUuid?.replace('new-chat-', '');
      const isNotCurrentChat = chat.uuid !== chatUuidWithoutPrefix;
      return hasActiveStream && isNotCurrentChat;
    });
  }, [chatUuid]);

  // Don't render anything if no orphaned streams or missing required context
  if (orphanedStreams.length === 0 || isEmpty(userUuid) || !authProvider) {
    return null;
  }

  browserLogger.debug('[Orphaned Streams Container] Found orphaned streams', {
    count: orphanedStreams.length,
    chatUuids: orphanedStreams.map((chat) => chat.uuid),
  });

  return (
    <>
      {orphanedStreams.map((chat) => (
        <OrphanedStreamProvider
          key={chat.uuid}
          chat={chat}
          userUuid={userUuid}
          authProvider={authProvider}
          scopes={userScopes}
          onOrphanedStreamFinish={notificationController.onOrphanedStreamFinish}
        />
      ))}
    </>
  );
}

/**
 * Provider wrapper for a single orphaned stream.
 * Fetches initial messages and wraps the recovery component in NovaChatProvider.
 */
function OrphanedStreamProvider({
  chat,
  userUuid,
  authProvider,
  scopes,
  onOrphanedStreamFinish,
}: {
  chat: GetChatsByFiltersChat;
  userUuid: string;
  authProvider: ReturnType<typeof useChatOverlayContext>['authProvider'];
  scopes?: string[];
  onOrphanedStreamFinish: (params: {
    chatUuid: string;
    chatTitle: string;
    isStandaloneMode?: boolean;
  }) => Promise<void>;
}) {
  const chatTitle = chat.title || 'New Chat';

  return (
    <NovaChatProvider
      userUuid={userUuid}
      accountUuid={chat.accountUuid ?? undefined}
      authProvider={authProvider}
      scopes={scopes}
      initialMessages={[]}
    >
      <OrphanedStreamRecovery
        chatUuid={chat.uuid!}
        chatTitle={chatTitle}
        initialMessages={[]}
        onStreamFinish={onOrphanedStreamFinish}
      />
    </NovaChatProvider>
  );
}
